// Wire shapes and staff-app checks: agent_docs/request_configuration_shapes.md

// Name staff-app configuration faults for Sentry grouping and to distinguish them from network TypeErrors.
export class InformationRequestConfigurationError extends Error {
  constructor(message) {
    super(message);
    this.name = "InformationRequestConfigurationError";
  }
}

const hasSteps = (configuration) => Object.prototype.hasOwnProperty.call(configuration, "steps");

const validateStepItem = (item, itemIdentifiers) => {
  if (item?.request_type !== "field" && item?.request_type !== "case_document") {
    throw new InformationRequestConfigurationError(
      `Information request step item must be a field or a case_document, received "${item?.request_type ?? ""}"`,
    );
  }
  if (typeof item.identifier !== "string" || item.identifier.length === 0) {
    throw new InformationRequestConfigurationError(
      "Information request step item identifier must be a non-empty string",
    );
  }
  // The staff app adds field_type from the field definition. Without it the
  // renderer draws nothing, and a required field then blocks submission with
  // no visible error.
  if (item.request_type === "field" && (typeof item.field_type !== "string" || item.field_type.length === 0)) {
    throw new InformationRequestConfigurationError(
      `Information request field item "${item.identifier}" must have a field_type`,
    );
  }
  if (itemIdentifiers.has(item.identifier)) {
    throw new InformationRequestConfigurationError(
      `Information request step item identifiers must be unique, received "${item.identifier}" more than once`,
    );
  }
  itemIdentifiers.add(item.identifier);
};

// Delete this projection with the legacy fields renderer. Wire keys remain on
// steps; only the temporary renderer field uses field_identifier.
const toLegacyRenderField = (item) => {
  const { request_type, identifier, ...attributes } = item;
  const field = { ...attributes, field_identifier: identifier };
  if (request_type === "case_document") {
    field.field_type = "document_upload";
  }
  return field;
};

const stepItems = (step) => {
  const items = step?.items;
  // An empty items list is valid: every item of the step is already closed. A null
  // step has no items array either, so it fails as a missing items array rather than
  // on the dereference.
  if (!Array.isArray(items)) {
    throw new InformationRequestConfigurationError("Information request step must contain an items array");
  }

  const itemIdentifiers = new Set();
  items.forEach((item) => validateStepItem(item, itemIdentifiers));

  return items;
};

// Only the browser checks the step shape. Go passes the configuration through as raw JSON.
const onlyStepItems = (steps) => {
  // Release two of the staff app permits more than one step, and the two apps deploy
  // separately. Without this a client on release one renders the first step, drops the
  // rest and submits as complete, so the client answers less than we asked with no error.
  // The optional chain keeps any other value a named error rather than a TypeError,
  // so Sentry groups it with the other configuration faults.
  if (steps?.length !== 1) {
    throw new InformationRequestConfigurationError(
      `Information request configuration must contain exactly one step, received ${
        Array.isArray(steps) ? steps.length : String(steps)
      }`,
    );
  }

  return stepItems(steps[0]);
};

// A legacy document_request snapshot has an empty fields list and a documents list
// with the same keys as a case_document step item. The completed view reads it once
// the definition has become steps and the doable has been re-sent as an
// information_request. The live legacy form never sends documents here.
const legacyRenderFields = (configuration) => [
  ...(configuration.fields ?? []),
  ...(configuration.documents ?? []).map((document) =>
    toLegacyRenderField({ request_type: "case_document", ...document }),
  ),
];

// Always provide fields because callers treat it as an array. The staff app omits the
// key when the client cannot complete the request, which is an empty request here.
export const normaliseInformationRequestConfiguration = (configuration) => {
  if (configuration == null) {
    return configuration;
  }

  if (!hasSteps(configuration)) {
    return { ...configuration, fields: legacyRenderFields(configuration) };
  }

  const items = onlyStepItems(configuration.steps);
  return { ...configuration, fields: items.map(toLegacyRenderField) };
};

export const formItems = (configuration) => {
  const fieldsByIdentifier = new Map((configuration?.fields ?? []).map((field) => [field.field_identifier, field]));
  return { fieldsByIdentifier, itemIdentifiers: [...fieldsByIdentifier.keys()] };
};
